import { fragmentAddress } from "@saleor/fragments/address";
import { fragmentOrderDetails } from "@saleor/fragments/orders";
import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import makeQuery from "@saleor/hooks/makeQuery";
import makeTopLevelSearch from "@saleor/hooks/makeTopLevelSearch";
import gql from "graphql-tag";

import { TypedQuery } from "../queries";
import {
  DownloadTotalCount,
  DownloadTotalCountVariables
} from "./types/DownloadTotalCount";
import {
  ExportOrderFile,
  ExportOrderFileVariables
} from "./types/ExportOrderFile";
import { GetGrabStatus, GetGrabStatusVariables } from "./types/GetGrabStatus";
import {
  MallFilterData,
  MallFilterDataVariables
} from "./types/MallFilterData";
import {
  MotherOrderList,
  MotherOrderListVariables
} from "./types/MotherOrderList";
import { OrderDetails, OrderDetailsVariables } from "./types/OrderDetails";
import {
  OrderDraftList,
  OrderDraftListVariables
} from "./types/OrderDraftList";
import {
  OrderFulfillData,
  OrderFulfillDataVariables
} from "./types/OrderFulfillData";
import {
  OrderLastRiderAssigned,
  OrderLastRiderAssignedVariables
} from "./types/OrderLastRiderAssigned";
import { RiderDetails, RiderDetailsVariables } from "./types/RiderDetails";
import {
  SearchOrderVariant as SearchOrderVariantType,
  SearchOrderVariantVariables
} from "./types/SearchOrderVariant";
import { searchRider, searchRiderVariables } from "./types/searchRider";

export const orderListQuery = gql`
  ${fragmentAddress}
  query MotherOrderList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: OrderFilterInput
    $sort: OrderSortingInput
  ) {
    orders(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
      sortBy: $sort
    ) {
      totalCount
      edges {
        node {
          binCode
          verticalType
          orderFulfillmentRemarks {
            id
            message
          }
          id
          mall {
            id
          }
          orderCode
          bookingFailedReason
          firstLevelCategory {
            itemType
            quantity
          }
          verticalType
          smopStatus
          subOrders {
            id
            orderCode
            prepTimeUpper
            prepTimeLower
            tenant {
              id
              storeName
              location
              brand {
                id
                name
              }
            }
          }
          created
          shippingSchedule
          shippingMethod {
            id
            deliveryType
          }
          payments {
            id
            paymentMethodType
            channelCode
          }
          total {
            __typename
            gross {
              __typename
              currency
              amount
            }
          }
          user {
            id
            firstName
            lastName
            email
            contactNo
            smac
          }
          shippingAddress {
            id
            streetAddress1
            streetAddress2
            unitDetails
            streetBrgy
            landmark
            city
            latitude
            longitude
          }
          billingAddress {
            ...AddressFragment
          }
          rider {
            id
            firstName
            lastName
            contactNo
          }
          isRider3pl
          thirdPartyRider {
            id
            firstName
            lastName
            contactNo
            deliveryCourier {
              senderContactNo
              senderFirstName
              senderLastName
              shippingCompany
              referenceNo
              notes
              status
              homeLocation {
                id
                name
              }
              failedReason
              dropOffLocation {
                id
                name
                code
                longitude
                latitude
                firstName
                lastName
                contactNo
                remarks
                dropOffAddress
                mall {
                  id
                  name
                }
              }
            }
          }
          deliveryCourier {
            status
            failedReason
            senderContactNo
            senderFirstName
            senderLastName
            dropOffLocation {
              id
              name
              code
              longitude
              latitude
              firstName
              lastName
              contactNo
              remarks
              dropOffAddress
              mall {
                id
                name
              }
            }
          }
          weight {
            value
            unit
          }
          thirdPartyAwbCode
          courier
          number
          paymentStatus
          status
          userEmail
          cancelReason
          isMultiTenant
          shippedAt
          completedAt
          dwellTime
          totalTime
          onTime
          aging
          lateDispatchReason
          lateDeliveryReason
          lastRiderAssignedAt
          remittanceManualAdjustmentStatus
          remittanceManualAdjustmentAmount
          remittanceManualAdjustmentTotalAmount
          remittanceLine {
            remittance {
              id
              remittanceNo
              totalAmount
              createdAt
              remittedAt
              status
            }
          }
          bookingTime
          bookingTaskId
          mall {
            name
            fbAutoBookingConfig {
              isEnabled
            }
            gmAutoBookingConfig {
              isEnabled
            }
          }
          __typename
        }
      }
      pageInfo {
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
      }
    }
  }
`;
export const useOrderListQuery = makeQuery<
  MotherOrderList,
  MotherOrderListVariables
>(orderListQuery);

export const orderDraftListQuery = gql`
  ${fragmentAddress}
  query OrderDraftList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: OrderDraftFilterInput
    $sort: OrderSortingInput
  ) {
    draftOrders(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
      sortBy: $sort
    ) {
      edges {
        node {
          __typename
          billingAddress {
            ...AddressFragment
          }
          created
          id
          number
          paymentStatus
          status
          total {
            __typename
            gross {
              __typename
              amount
              currency
            }
          }
          userEmail
        }
      }
      pageInfo {
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
      }
    }
  }
`;
export const useOrderDraftListQuery = makeQuery<
  OrderDraftList,
  OrderDraftListVariables
>(orderDraftListQuery);

export const orderDetailsQuery = gql`
  ${fragmentOrderDetails}
  query OrderDetails($id: ID!, $getTimeout: Boolean) {
    order(id: $id, getTimeout: $getTimeout) {
      ...OrderDetailsFragment
      thirdPartyAwbCode
      courier
      bin {
        id
        rack {
          id
        }
        binNo
        isAvailable
        isVisible
      }
      binCode
      deliveryCourier {
        senderContactNo
        senderFirstName
        senderLastName
        status
        failedReason
        trackingUrl
        dropOffLocation {
          id
          name
          code
          longitude
          latitude
          firstName
          lastName
          contactNo
          remarks
          dropOffAddress
        }
      }
      firstLevelCategory {
        itemType
        quantity
      }
      weight {
        value
        unit
      }
      bookingTime
      bookingTaskId
      dropOffLocation {
        id
        name
        code
        longitude
        latitude
        firstName
        lastName
        contactNo
        remarks
        dropOffAddress
      }
      remittanceManualAdjustmentStatus
      remittanceManualAdjustmentAmount
      remittanceManualAdjustmentTotalAmount
      remittanceManualAdjustments {
        adjustedAmount
        adjustedTotalAmount
      }
      assignRiderCardInactiveTimeout
      remittanceLine {
        remittance {
          id
          remittanceNo
          status
        }
      }
    }
    shop {
      countries {
        code
        country
      }
      defaultWeightUnit
    }
  }
`;
export const TypedOrderDetailsQuery = TypedQuery<
  OrderDetails,
  OrderDetailsVariables
>(orderDetailsQuery);

export const useOrderDetailsQuery = makeQuery<
  OrderDetails,
  OrderDetailsVariables
>(orderDetailsQuery);

export const searchOrderVariant = gql`
  query SearchOrderVariant($first: Int!, $query: String!, $after: String) {
    search: products(first: $first, after: $after, filter: { search: $query }) {
      edges {
        node {
          id
          name
          thumbnail {
            url
          }
          variants {
            id
            name
            sku
            pricing {
              priceUndiscounted {
                net {
                  amount
                  currency
                }
              }
            }
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
    }
  }
`;
export const useOrderVariantSearch = makeTopLevelSearch<
  SearchOrderVariantType,
  SearchOrderVariantVariables
>(searchOrderVariant);

const orderFulfillData = gql`
  query OrderFulfillData($orderId: ID!) {
    order(id: $orderId) {
      id
      lines {
        id
        isShippingRequired
        productName
        quantity
        allocations {
          quantity
          warehouse {
            id
          }
        }
        quantityFulfilled
        variant {
          id
          name
          sku
          attributes {
            values {
              id
              name
            }
          }
          stocks {
            id
            warehouse {
              id
            }
            quantity
            quantityAllocated
          }
          trackInventory
        }
        thumbnail(size: 64) {
          url
        }
      }
      number
    }
  }
`;
export const useOrderFulfillData = makeQuery<
  OrderFulfillData,
  OrderFulfillDataVariables
>(orderFulfillData);

export const searchRiderQuery = gql`
  ${pageInfoFragment}
  query searchRider(
    $after: String
    $first: Int!
    $query: String!
    $userType: [SmopUserTypeCode]
    $status: LockStatus
    $malls: [ID]
  ) {
    search: staffUsers(
      after: $after
      first: $first
      filter: {
        search: $query
        smopUserType: $userType
        status: $status
        malls: $malls
      }
    ) {
      edges {
        node {
          id
          email
          firstName
          lastName
          isActive
          avatar {
            alt
            url
          }
          contactNo
          dateJoined
          status
          defaultMall {
            name
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;

export const useSearchRider = makeTopLevelSearch<
  searchRider,
  searchRiderVariables
>(searchRiderQuery);

export const riderDetailsQuery = gql`
  query RiderDetails($id: ID!, $getTimeout: Boolean) {
    order(id: $id, getTimeout: $getTimeout) {
      id
      smopStatus
      cancelReason
      created
      verticalType
      rider {
        id
        firstName
        lastName
        contactNo
      }
      deliveryCourier {
        status
        failedReason
        senderContactNo
        senderFirstName
        senderLastName
        trackingUrl
        dropOffLocation {
          id
          name
          code
          longitude
          latitude
          firstName
          lastName
          contactNo
          remarks
          dropOffAddress
        }
      }
      isRider3pl
      dropOffLocation {
        id
        name
        code
        longitude
        latitude
        firstName
        lastName
        contactNo
        remarks
        dropOffAddress
      }
      thirdPartyRider {
        id
        firstName
        lastName
        contactNo
        trackingUrl
        deliveryCourier {
          trackingUrl
          senderContactNo
          senderFirstName
          senderLastName
          status
          failedReason
          shippingCompany
          referenceNo
          notes
          homeLocation {
            id
            name
          }
          riderPickUpLocation {
            id
            latitude
            longitude
            name
            remarks
          }
        }
      }
      thirdPartyAwbCode
      weight {
        value
        unit
      }
      bookingTime
      bookingTaskId
      courier
      shippedAt
      completedAt
      shippingMethod {
        id
        deliveryType
      }
      dwellTime
      totalTime
      onTime
      lastRiderAssignedAt
      lateDeliveryReason
      lateDispatchReason
      aging
      mall {
        id
        name
        address {
          id
          streetAddress1
          streetAddress2
          cityArea
          city
          countryArea
          postalCode
        }
        warehouses(first: 1) {
          edges {
            node {
              id
              name
              homeLocations {
                id
                name
                longitude
                latitude
                remarks
              }
            }
          }
        }
      }
      assignRiderCardInactiveTimeout
    }
  }
`;

export const useRiderDetailsQuery = makeQuery<
  RiderDetails,
  RiderDetailsVariables
>(riderDetailsQuery);

export const exportFile = gql`
  query ExportOrderFile($id: ID!) {
    exportFile(id: $id) {
      id
      status
      url
    }
  }
`;

export const useExportFile = makeQuery<
  ExportOrderFile,
  ExportOrderFileVariables
>(exportFile);

export const downloadTotalCount = gql`
  query DownloadTotalCount($filter: StoreOrderFilterInput) {
    storeOrders(filter: $filter) {
      totalCount
    }
  }
`;

export const useDownloadTotalCount = makeQuery<
  DownloadTotalCount,
  DownloadTotalCountVariables
>(downloadTotalCount);

export const getGrabStatus = gql`
  query GetGrabStatus($id: ID!) {
    order(id: $id) {
      thirdPartyRider {
        deliveryCourier {
          status
        }
      }
    }
  }
`;

export const useGetGrabStatus = makeQuery<
  GetGrabStatus,
  GetGrabStatusVariables
>(getGrabStatus);

export const mallFilterData = gql`
  query MallFilterData($mall: [ID], $first: Int!) {
    malls(first: $first, filter: { id: $mall }) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

export const useMallFilterData = makeQuery<
  MallFilterData,
  MallFilterDataVariables
>(mallFilterData);

export const orderLastRiderAssigned = gql`
  query OrderLastRiderAssigned($id: ID!) {
    order(id: $id) {
      lastRiderAssignedAt
    }
  }
`;

export const useOrderLastRiderAssigned = makeQuery<
  OrderLastRiderAssigned,
  OrderLastRiderAssignedVariables
>(orderLastRiderAssigned);
